import { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  TrendUp, Envelope, Users, CurrencyDollar, ChartLineUp, ArrowRight, Sparkle,
} from "@phosphor-icons/react";

const STAGE_ORDER = ["waiting", "qualified", "priority", "invited", "activated", "converted", "customer"];
const STAGE_LABEL = { waiting: "Waiting", qualified: "Qualified", priority: "Priority", invited: "Invited", activated: "Activated", converted: "Converted", customer: "Customer" };
const STAGE_COLOR = {
  waiting: "bg-slate-500", qualified: "bg-sky-500", priority: "bg-gold",
  invited: "bg-indigo-500", activated: "bg-teal", converted: "bg-emerald-500", customer: "bg-green-600",
};

export default function RevenueReadiness() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/revenue-readiness");
        setData(data);
      } catch (e) { toast.error(extractApiError(e, "Could not load revenue dashboard.")); }
      setBusy(false);
    })();
  }, []);

  if (busy || !data) return <div className="p-8 text-muted-foreground text-sm" data-testid="rr-loading">Loading revenue readiness…</div>;

  const wl = data.waitlist;
  const em = data.email_engagement;
  const sub = data.subscription;
  const maxFunnel = Math.max(...STAGE_ORDER.map((s) => wl.counts[s] || 0), 1);

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="revenue-readiness-page">
      <PageHeader
        eyebrow="Revenue readiness"
        title="Commercial dashboard"
        subtitle="Waitlist size, invitation & conversion rates, email engagement, subscription revenue — all metrics come from real platform data."
      />

      {/* Hero KPIs */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={CurrencyDollar} label="MRR" value={`${(sub.mrr_xof || 0).toLocaleString()} XOF`} sub={`ARR ${(sub.arr_xof || 0).toLocaleString()} XOF`} tone="teal" testid="rr-mrr" />
        <Kpi icon={Users}          label="Waitlist size" value={wl.size} sub={`${wl.consent_rate}% consent`} tone="primary" testid="rr-wl-size" />
        <Kpi icon={ChartLineUp}    label="Invitation rate" value={`${wl.invitation_rate}%`} sub={`${wl.invited} invited / ${wl.size} total`} tone="gold" testid="rr-inv-rate" />
        <Kpi icon={TrendUp}        label="Conversion-ready" value={wl.conversion_ready} sub="Consent + qualified" tone="emerald" testid="rr-conv-ready" />
      </div>

      {/* Funnel bars */}
      <section className="mt-10 bg-card border border-border rounded-2xl p-6 sm:p-8" aria-labelledby="rr-funnel-title" data-testid="rr-funnel">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center"><ChartLineUp size={18} weight="duotone" /></div>
          <h2 id="rr-funnel-title" className="font-display text-xl tracking-tight">Waitlist funnel · 7 stages</h2>
        </div>
        <div className="space-y-3">
          {STAGE_ORDER.map((s) => {
            const n = wl.counts[s] || 0;
            const pct = (n / maxFunnel) * 100;
            return (
              <div key={s} className="flex items-center gap-3">
                <div className="w-28 text-xs font-semibold">{STAGE_LABEL[s]}</div>
                <div className="flex-1 h-6 bg-secondary rounded-full overflow-hidden">
                  <div className={`h-full ${STAGE_COLOR[s]} transition-all duration-500 rounded-full`} style={{ width: `${pct}%` }} />
                </div>
                <div className="w-12 text-right data-large text-sm">{n}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Email engagement */}
      <section className="mt-10 bg-card border border-border rounded-2xl p-6 sm:p-8" data-testid="rr-emails">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-gold-soft text-gold grid place-items-center"><Envelope size={18} weight="duotone" /></div>
          <h2 className="font-display text-xl tracking-tight">Email engagement</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <MiniStat label="Sent" value={em.sent} />
          <MiniStat label="Delivered" value={em.delivered} sub={`${em.delivery_rate}%`} />
          <MiniStat label="Opened" value={em.opened} sub={`${em.open_rate}% open rate`} />
          <MiniStat label="Clicked" value={em.clicked} sub={`${em.click_rate}% click rate`} />
          <MiniStat label="Delivery rate" value={`${em.delivery_rate}%`} sub="Resend + outbox" />
        </div>
        <p className="mt-5 text-[11px] text-muted-foreground leading-relaxed">
          Open + click stats require Resend webhook configuration. Until webhooks are wired, deliveries are counted from the local <code>email_outbox</code> and opens remain at 0 (they will populate automatically once Resend event webhooks are enabled).
        </p>
      </section>

      {/* Subscriptions */}
      <section className="mt-10 bg-card border border-border rounded-2xl p-6 sm:p-8" data-testid="rr-subs">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-teal-soft text-teal grid place-items-center"><CurrencyDollar size={18} weight="duotone" /></div>
          <h2 className="font-display text-xl tracking-tight">Subscription revenue · realtime</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <MiniStat label="Total orgs" value={sub.total_orgs} />
          <MiniStat label="Active paid" value={sub.active} />
          <MiniStat label="Starter" value={sub.starter_orgs} sub="25 000 XOF each" />
          <MiniStat label="Professional" value={sub.professional_orgs} sub="85 000 XOF each" />
          <MiniStat label="Enterprise" value={sub.enterprise_orgs} sub="Custom" />
          <MiniStat label="ARR" value={`${(sub.arr_xof || 0).toLocaleString()} XOF`} sub="MRR × 12" tone="text-primary" />
        </div>
      </section>

      <div className="mt-8 text-xs text-muted-foreground text-right">Generated {new Date(data.generated_at).toLocaleString()}</div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "primary", testid }) {
  const bg = { teal: "bg-teal-soft text-teal", primary: "bg-primary/10 text-primary", gold: "bg-gold-soft text-gold", emerald: "bg-emerald-100 text-emerald-700" }[tone];
  return (
    <div className="bg-card border border-border rounded-2xl p-5 card-elev" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${bg} mb-4`}><Icon size={20} weight="duotone" /></div>
      <div className="text-overline text-muted-foreground">{label}</div>
      <div className="mt-1 data-large text-3xl">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub, tone = "text-foreground" }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 data-large text-xl ${tone}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
